import React, {
	forwardRef,
	SyntheticEvent,
	useEffect,
	useImperativeHandle,
	useMemo,
	useRef,
	useState,
} from 'react';

import { SpinnerOverlay } from '@components/common';

import { convertToBarTasks } from './helpers/barHelper';
import { ganttDateRange, seedDates } from './helpers/dateHelper';
import { removeHiddenTasks, sortTasks } from './helpers/otherHelper';

import { BarTask } from '@interfaces/ganttTasks/barTask';
import { DateSetup } from '@interfaces/ganttTasks/dateSetup';
import { GanttEvent } from '@interfaces/ganttTasks/ganttTaskActions';
import { GanttHandle, GanttProps, Task } from '@interfaces/ganttTasks/ganttTasks';

import { EViewMode } from '@enums/ganttTasks';

import colors from '@constants/colors';
import {
	defaultArrowIndent,
	defaultBarCornerRadius,
	defaultBarFill,
	defaultColumnWidth,
	defaultHandleWidth,
	defaultHeaderHeight,
	defaultRowHeight,
} from './constants';

import { CalendarProps } from './Calendar/Calendar';
import { GridProps } from './Grid/Grid';
import { TaskGanttContentProps } from './TaskGantt/TaskGanttContent/TaskGanttContent';
import TaskListHeaderDefault from './TaskList/TaskListHeader';
import TaskListTableDefault from './TaskList/TaskListTable';
import HorizontalScroll from './HorizontalScroll';
import StandardTooltipContent from './StandardTooltipContent';
import TaskGantt from './TaskGantt';
import TaskList from './TaskList';
import Tooltip from './Tooltip';
import VerticalScroll from './VerticalScroll';

import styles from './Gantt.module.scss';

const Gantt = forwardRef<GanttHandle, GanttProps>(({
	tasks,
	headerHeight = defaultHeaderHeight,
	columnWidth = defaultColumnWidth,
	isTasksList,
	rowHeight = defaultRowHeight,
	ganttHeight = 0,
	viewMode = EViewMode.Week,
	preStepsCount = 1,
	locale = 'en-GB',
	barFill = defaultBarFill,
	barCornerRadius = defaultBarCornerRadius,
	handleWidth = defaultHandleWidth,
	arrowIndent = defaultArrowIndent,
	todayColor = colors.indigo05,
	viewDate,
	TooltipContent = StandardTooltipContent,
	TaskListHeader = TaskListHeaderDefault,
	TaskListTable = TaskListTableDefault,
	onClick,
	onExpanderClick,
	isLoading,
	emptyText,
}, ref) => {
	const wrapperRef = useRef<HTMLDivElement>(null);
	const taskListRef = useRef<HTMLDivElement>(null);
	const [dateSetup, setDateSetup] = useState<DateSetup>(() => {
		const [startDate, endDate] = ganttDateRange(tasks, viewMode, preStepsCount);
		return { viewMode, dates: seedDates(startDate, endDate, viewMode) };
	});
	const [currentViewDate, setCurrentViewDate] = useState<Date | undefined>(undefined);

	const [taskListWidth, setTaskListWidth] = useState(0);
	const [svgContainerWidth, setSvgContainerWidth] = useState(0);
	const [svgContainerHeight, setSvgContainerHeight] = useState(ganttHeight);
	const [barTasks, setBarTasks] = useState<BarTask[]>([]);
	const [ganttEvent, setGanttEvent] = useState<GanttEvent>({
		action: '',
	});
	const taskHeight = useMemo(
		() => (rowHeight * barFill) / 100,
		[rowHeight, barFill],
	);

	const [failedTask, setFailedTask] = useState<BarTask | null>(null);

	const svgWidth = dateSetup.dates.length * columnWidth;
	const ganttFullHeight = barTasks.length * rowHeight;

	const [scrollY, setScrollY] = useState(0);
	const [scrollX, setScrollX] = useState(-1);
	const [todayColumnScrollX, setTodayColumnScrollX] = useState<number>(0);
	const [ignoreScrollEvent, setIgnoreScrollEvent] = useState(false);

	useImperativeHandle(ref, () => ({
		scrollToToday: () => {
			setScrollX(todayColumnScrollX);
		},
	}), [todayColumnScrollX]);

	// task change events
	useEffect(() => {
		let filteredTasks: Task[];
		if (onExpanderClick) {
			filteredTasks = removeHiddenTasks(tasks);
		} else {
			filteredTasks = tasks;
		}
		filteredTasks = filteredTasks.sort(sortTasks);
		const [startDate, endDate] = ganttDateRange(
			filteredTasks,
			viewMode,
			preStepsCount,
		);
		const newDates = seedDates(startDate, endDate, viewMode);
		setDateSetup({ dates: newDates, viewMode });
		setBarTasks(
			convertToBarTasks(
				filteredTasks,
				newDates,
				columnWidth,
				rowHeight,
				taskHeight,
				barCornerRadius,
				handleWidth,
			),
		);
	}, [
		tasks,
		viewMode,
		preStepsCount,
		rowHeight,
		barCornerRadius,
		columnWidth,
		taskHeight,
		handleWidth,
		scrollX,
		onExpanderClick,
	]);

	useEffect(() => {
		if (
			viewMode === dateSetup.viewMode &&
			((viewDate && !currentViewDate) ||
				(viewDate && currentViewDate?.valueOf() !== viewDate.valueOf()))
		) {
			const dates = dateSetup.dates;
			const index = dates.findIndex(
				(d, i) =>
					viewDate.valueOf() >= d.valueOf() &&
					i + 1 !== dates.length &&
					viewDate.valueOf() < dates[i + 1].valueOf(),
			);
			if (index === -1) {
				return;
			}
			setCurrentViewDate(viewDate);
			setScrollX(columnWidth * index);
		}
	}, [
		viewDate,
		columnWidth,
		dateSetup.dates,
		dateSetup.viewMode,
		viewMode,
		currentViewDate,
		setCurrentViewDate,
	]);

	useEffect(() => {
		if (failedTask) {
			setBarTasks(barTasks.map(t => (t.id !== failedTask.id ? t : failedTask)));
			setFailedTask(null);
		}
	}, [failedTask, barTasks]);

	useEffect(() => {
		if (!isTasksList) {
			setTaskListWidth(0);
		}

		if (taskListRef.current) {
			setTaskListWidth(taskListRef.current.offsetWidth);
		}
	}, [taskListRef, isTasksList]);

	useEffect(() => {
		if (wrapperRef.current) {
			setSvgContainerWidth(wrapperRef.current.offsetWidth - taskListWidth);
		}
	}, [wrapperRef, taskListWidth]);

	useEffect(() => {
		if (ganttHeight) {
			setSvgContainerHeight(ganttHeight + headerHeight);
		} else {
			setSvgContainerHeight(tasks.length * rowHeight + headerHeight);
		}
	}, [ganttHeight, tasks, headerHeight, rowHeight]);

	// scroll events
	useEffect(() => {
		const wrapperElement = wrapperRef.current;

		if (!wrapperElement) return;

		const handleWheel = (event: WheelEvent) => {
			if (event.shiftKey || event.deltaX) {
				const scrollMove = event.deltaX ? event.deltaX : event.deltaY;
				let newScrollX = scrollX + scrollMove;
				if (newScrollX < 0) {
					newScrollX = 0;
				} else if (newScrollX > svgWidth) {
					newScrollX = svgWidth;
				}
				setScrollX(newScrollX);
				event.preventDefault();
			} else if (ganttHeight) {
				let newScrollY = scrollY + event.deltaY;
				if (newScrollY < 0) {
					newScrollY = 0;
				} else if (newScrollY > ganttFullHeight - ganttHeight) {
					newScrollY = ganttFullHeight - ganttHeight;
				}
				if (newScrollY !== scrollY) {
					setScrollY(newScrollY);
					event.preventDefault();
				}
			}

			setIgnoreScrollEvent(true);
		};

		// subscribe if scroll is necessary
		wrapperElement.addEventListener('wheel', handleWheel, {
			passive: false,
		});

		return () => {
			wrapperElement.removeEventListener('wheel', handleWheel);
		};
	}, [
		wrapperRef,
		scrollY,
		scrollX,
		ganttHeight,
		svgWidth,
		ganttFullHeight,
	]);

	const handleScrollY = (event: SyntheticEvent<HTMLDivElement>) => {
		if (scrollY !== event.currentTarget.scrollTop && !ignoreScrollEvent) {
			setScrollY(event.currentTarget.scrollTop);
			setIgnoreScrollEvent(true);
		} else {
			setIgnoreScrollEvent(false);
		}
	};

	const handleScrollX = (event: SyntheticEvent<HTMLDivElement>) => {
		if (scrollX !== event.currentTarget.scrollLeft && !ignoreScrollEvent) {
			setScrollX(event.currentTarget.scrollLeft);
			setIgnoreScrollEvent(true);
		} else {
			setIgnoreScrollEvent(false);
		}
	};

	/**
	 * Handles arrow keys events and transform it to new scroll
	 */
	const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
		event.preventDefault();
		let newScrollY = scrollY;
		let newScrollX = scrollX;
		let isX = true;
		switch (event.key) {
			// IE/Edge specific value
			case 'Down':
			case 'ArrowDown':
				newScrollY += rowHeight;
				isX = false;
				break;
			// IE/Edge specific value
			case 'Up':
			case 'ArrowUp':
				newScrollY -= rowHeight;
				isX = false;
				break;
			case 'Left':
			case 'ArrowLeft':
				newScrollX -= columnWidth;
				break;
			// IE/Edge specific value
			case 'Right':
			case 'ArrowRight':
				newScrollX += columnWidth;
				break;
		}
		if (isX) {
			if (newScrollX < 0) {
				newScrollX = 0;
			} else if (newScrollX > svgWidth) {
				newScrollX = svgWidth;
			}
			setScrollX(newScrollX);
		} else {
			if (newScrollY < 0) {
				newScrollY = 0;
			} else if (newScrollY > ganttFullHeight - ganttHeight) {
				newScrollY = ganttFullHeight - ganttHeight;
			}
			setScrollY(newScrollY);
		}
		setIgnoreScrollEvent(true);
	};

	const handleExpanderClick = (task: Task) => {
		if (onExpanderClick && task.hideChildren !== undefined) {
			onExpanderClick({ ...task, hideChildren: !task.hideChildren });
		}
	};

	const gridProps: GridProps = {
		columnWidth,
		svgWidth,
		tasks: tasks,
		rowHeight,
		dates: dateSetup.dates,
		todayColor,
		setTodayColumnScrollX,
	};

	const calendarProps: CalendarProps = {
		dateSetup,
		locale,
		viewMode,
		headerHeight,
		columnWidth,
	};

	const barProps: TaskGanttContentProps = {
		tasks: barTasks,
		ganttEvent,
		rowHeight,
		taskHeight,
		arrowIndent,
		setGanttEvent,
		onClick,
	};

	return (
		<>
			<div
				ref={wrapperRef}
				className={styles.wrapper}
				onKeyDown={handleKeyDown}
				tabIndex={0}
			>
				{isLoading && <SpinnerOverlay />}
				{isTasksList
					&& <TaskList
						TaskListHeader={TaskListHeader}
						TaskListTable={TaskListTable}
						emptyText={emptyText}
						ganttHeight={ganttHeight}
						headerHeight={headerHeight}
						horizontalContainerClass={styles.horizontalContainer}
						isLoading={isLoading}
						onExpanderClick={handleExpanderClick}
						rowHeight={rowHeight}
						scrollY={scrollY}
						taskListRef={taskListRef}
						tasks={barTasks}
					/>
				}
				<TaskGantt
					barProps={barProps}
					calendarProps={calendarProps}
					ganttHeight={ganttHeight}
					gridProps={gridProps}
					scrollX={scrollX}
					scrollY={scrollY}
				/>
				{ganttEvent.changedTask && (
					<Tooltip
						TooltipContent={TooltipContent}
						arrowIndent={arrowIndent}
						headerHeight={headerHeight}
						rowHeight={rowHeight}
						scrollX={scrollX}
						scrollY={scrollY}
						svgContainerHeight={svgContainerHeight}
						svgContainerWidth={svgContainerWidth}
						svgWidth={svgWidth}
						task={ganttEvent.changedTask}
						taskListWidth={taskListWidth}
					/>
				)}
				<VerticalScroll
					ganttFullHeight={ganttFullHeight}
					ganttHeight={ganttHeight}
					headerHeight={headerHeight}
					onScroll={handleScrollY}
					scroll={scrollY}
				/>
			</div>
			<HorizontalScroll
				onScroll={handleScrollX}
				scroll={scrollX}
				svgWidth={svgWidth}
				taskListWidth={taskListWidth}
			/>
		</>
	);
});

Gantt.displayName = 'Gantt';

export default Gantt;
